// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Skeleton from "@awc/App/Components/Skeleton/Skeleton";
import { CustomPageLinkModel } from "@awc/App/Services/CustomPageLinksService";
import { CustomAppState } from "@awc/App/Store/Reducers";
import { XNGAGE_ELEMENTS } from "@awc/XDL/constants";
import { color } from "@awc/XDL/Utilities";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import Accordion from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    nodeId = "nodeId",
    marginBottom = "marginBottom",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.nodeId]: LinkFieldValue;
        [fields.marginBottom]: number;
        [fields.customCSS]: string;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const currentPage = getCurrentPage(state);
    return {
        rootPageLink: getPageLinkByNodeId(state, ownProps.fields.nodeId?.value || currentPage.nodeId),
        currentPage,
        areCustomPageLinksLoading: (state as CustomAppState).xCustomPageLinks?.areCustomPageLinksLoading,
    };
};

interface ListStyleProp {
    marginBottom: number;
}

const ListStyle = styled.ul<ListStyleProp>`
    li {
        margin-bottom: ${props => props.marginBottom}px;
    }
`;

const SkeletonItem = styled.li`
    padding: 20px 12px;
    border-top: 1px solid ${color("common.border")};
`;
const mapDispatchToProps = {};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface AlliedUniversitySideNavigationListStyles {
    link?: LinkPresentationProps;
    splitter?: InjectableCss;
    linkAccordionSection?: AccordionSectionPresentationProps;
}

export const alliedUniversitySideNavigationListStyles: AlliedUniversitySideNavigationListStyles = {};

export const styles = alliedUniversitySideNavigationListStyles;

export const AlliedUniversitySideNavigationList = (props: Props) => {
    if (!props.rootPageLink || !props.rootPageLink.children) {
        return null;
    }

    const customCssWrapper = {
        css: css`
            ${props.fields.customCSS}
        `,
    };

    const renderLink = (pageLink: CustomPageLinkModel, currentDepth: number, showAccordion?: boolean) => {
        // match classic depth
        if (currentDepth > 2) {
            return;
        }

        if (pageLink.excludeFromNavigation) {
            return;
        }

        if (pageLink.type === "VmiUsersPage") {
            const currentMode = getCookie(NavigationModeCookieName);
            if (!currentMode || currentMode === "Storefront") {
                return;
            }
        }

        const nextDepth = currentDepth + 1;
        const listId = pageLink.title.replace(/\s/g, "").trim();
        const childrenList = pageLink.children && pageLink.children.length > 0 && (
            <ListStyle
                marginBottom={props.fields.marginBottom}
                key={`children-${pageLink.id}`}
                aria-labelledby={listId}
            >
                {pageLink.children.map(child => renderLink(child, nextDepth, false))}
            </ListStyle>
        );
        return (
            <li key={pageLink.id} className="au-navigation-list-item">
                {showAccordion ? (
                    <Accordion headingLevel={4}>
                        <ManagedAccordionSection
                            title={pageLink.overrideTitle || pageLink.title}
                            initialExpanded={props.currentPage.name === pageLink.title}
                            {...styles.linkAccordionSection}
                        >
                            {childrenList}
                        </ManagedAccordionSection>
                    </Accordion>
                ) : (
                    <Link
                        className="au-navigation-list-link"
                        data-test-selector={`AuNavigationList-${pageLink.title}`}
                        href={pageLink.url}
                        {...styles.link}
                        {...{ isSubLink: currentDepth === 2 }}
                        aria-label={pageLink.title}
                        id={listId}
                    >
                        {pageLink.overrideTitle || pageLink.title}
                    </Link>
                )}
            </li>
        );
    };

    const marginBottom = props.fields.marginBottom || 0;
    const linksToRender = props.rootPageLink.children.map(link => renderLink(link, 1, true)).filter(Boolean);

    return (
        <StyledWrapper {...customCssWrapper}>
            <ListStyle className="au-navigation-list" marginBottom={marginBottom} data-test-selector="auNavigationList">
                {props.areCustomPageLinksLoading
                    ? Array(linksToRender.length)
                          .fill(undefined)
                          .map((_, i) => (
                              <SkeletonItem key={i}>
                                  <Skeleton height="18px" width="100%" />
                              </SkeletonItem>
                          ))
                    : linksToRender.map(link => link)}
            </ListStyle>
            <StyledWrapper {...styles.splitter} {...{ marginBottom }} />
        </StyledWrapper>
    );
};

const defaultCustomCss = `.au-navigation-list{
}

.au-navigation-list-item {
}

.au-navigation-list-link {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AlliedUniversitySideNavigationList),
    definition: {
        group: XNGAGE_ELEMENTS,
        icon: "link-simple",
        fieldDefinitions: [
            {
                name: fields.nodeId,
                displayName: "Page",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                allowUrls: item => false,
                allowCategories: item => false,
                tab: basicTab,
            },
            {
                name: fields.marginBottom,
                displayName: "Margin Bottom",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 100,
                min: 0,
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
